import { useEffect, useState } from 'react'
import { createContainer } from 'unstated-next'
import TokenStorage from '../Api/TokenStorage'
import AuthTokenExpiredError from '../Api/AuthTokenExpiredError'
import GuestReestablishRequiredError from '../Api/GuestReestablishRequiredError'
import anearApi from '../Api/AnearApi'
import * as logger from 'loglevel'
import userMessaging from '../Utils/UserMessaging'

const useUserAuth = (initialState = null) => {
  // Use provided initialState (from Provider) or default empty state
  const defaultState = {
    user: null,
    avatarIndex: null,
    position: {}
  }
  const resolvedInitialState = initialState || defaultState

  const tokenStorage = new TokenStorage()
  const [state, setState] = useState(resolvedInitialState)
  const [guestReestablishMeta, setGuestReestablishMeta] = useState(null)

  const gset = newState => {
    setState((currentState) => ({ ...currentState, ...newState }))
  }

  const setPosition = async () => {
    let geoPosition = {}
    let errMsg = ''

    try {
      geoPosition = await anearApi.getPosition()
    } catch (err) {
      errMsg = `[${err}]`
      geoPosition = {}
    }
    logger.info(`geoPosition: ${errMsg}`, geoPosition)
    gset({ position: geoPosition })
  }

  const setUser = async user => {
    // Close any existing realtime connection before initializing with new user
    userMessaging.closeRealtime()
    await userMessaging.initRealtime()
    if (tokenStorage.hasGuestToken()) {
      tokenStorage.setGuestIdentitySnapshot({
        id: user?.id || null,
        name: user?.name?.() || null,
        avatarUrl: user?.avatarUrl?.() || null,
        createdAt: user?.memberSince?.() || null,
        claimedAt: user?.claimedAt?.() || null,
      })
    }
    gset({ user })
    logger.info(`${user.name()} account set, MobileSafari=${mobileSafari()}`)
  }

  const loadUser = async (force = false) => {
    //
    // Only bootstrap the current user from /v1/accounts if we either:
    //  - explicitly force a reload, or
    //  - already have an auth token stored (guest or registered).
    //
    // This avoids auto-creating a guest user on first load of public
    // routes like Home or Apps. Guest/user creation should happen
    // explicitly via guest-signup or other flows.
    //
    if (!force && isAuthed()) return

    if (!tokenStorage.hasAuthMeta()) return

    if (tokenStorage.isStoredAuthTokenExpired()) {
      throw new AuthTokenExpiredError('Stored auth token is expired or invalid')
    }

    const user = await anearApi.getAccount()
    await setUser(user)
  }

  //
  // On initial mount, if we already have an auth token (guest or registered)
  // from a previous session, eagerly bootstrap the current user from
  // /v1/accounts. This keeps guest/registered users signed in across full
  // page reloads without auto-creating a guest for first-time visitors.
  //
  // IMPORTANT: If initialState is provided (e.g., from a parent Provider),
  // skip loadUser() to avoid closing/reinitializing Ably Realtime singleton.
  // This is critical for child event rendering in Shadow DOM, as the singleton
  // Ably connection must remain open for both parent and child events.
  //
  useEffect(() => {
    // Legacy hygiene: selfie marker keys are no longer used.
    tokenStorage.clearAllLegacyGuestHasSelfie()

    // Skip bootstrap if initialState was provided (user already loaded from parent)
    // This prevents Ably from being closed/reopened when rendering child apps
    if (initialState !== null && initialState !== undefined) {
      logger.debug('UserAuthContainer: Skipping loadUser() - initialState provided (user may be null or loaded)')
      return
    }

    loadUser().catch((err) => {
      if (err instanceof GuestReestablishRequiredError) {
        setGuestReestablishMeta(err.meta || {})
        return
      }

      if (err instanceof AuthTokenExpiredError) {
        recoverFromStaleAuth(err.message)
        return
      }

      logger.warn('User bootstrap failed during initial load', err)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const clearUser = () => {
    gset({ user: null })
  }

  const hasRegisteredToken = () => {
    return tokenStorage.hasRegisteredToken()
  }

  const authMeta = () => {
    return tokenStorage.getAuthMeta()
  }

  const acctType = () => {
    return tokenStorage.acctType()
  }

  const authTokenPayload = () => {
    return tokenStorage.decodeAuthTokenPayload()
  }

  const isAuthed = () => {
    // user defined in state AND a token backing it in localStorage
    return state.user !== null && tokenStorage.getTokenKey() !== null
  }

  const isRegisteredAuthed = () => {
    return isAuthed() && hasRegisteredToken()
  }

  const isGuestAuthed = () => {
    return isAuthed() && tokenStorage.hasGuestToken()
  }

  const clearAuthToken = () => {
    return tokenStorage.clearAuthMeta()
  }

  const dismissGuestReestablish = () => {
    // GuestReestablishGate sends public routes, including /home, /login,
    // /signup, and /guest-signup, back to the welcome while this is set.
    // Clear it before leaving that screen or the next navigation bounces.
    setGuestReestablishMeta(null)
    tokenStorage.clearGuestIdentitySnapshot()
  }

  const clearUserAndToken = () => {
    const userId = state?.user?.id ? String(state.user.id) : null
    tokenStorage.clearLegacyGuestHasSelfieByUserId(userId)
    dismissGuestReestablish()

    userMessaging.closeRealtime()
    clearUser()
    clearAuthToken()
  }

  const recoverFromStaleAuth = (reason) => {
    logger.info(`Clearing stale auth token${reason ? `: ${reason}` : ''}`)
    clearUserAndToken()
  }

  const hasAuthToken = () => {
    return tokenStorage.hasAuthMeta()
  }

  const anearUser = () => {
    return state.user
  }

  const roles = () => {
    const user = anearUser()
    const userRoles = user ? user.roles() : []
    const tokenRoles = authTokenPayload()?.roles
    const merged = [...new Set([...(Array.isArray(userRoles) ? userRoles : []), ...(Array.isArray(tokenRoles) ? tokenRoles : [])])]
    return merged
  }

  const hasRole = (roleName) => {
    return roles().includes(roleName)
  }

  const isAdmin = () => {
    return hasRole('admin')
  }

  const isDeveloper = () => {
    return hasRole('developer')
  }

  const isMerchant = () => {
    return hasRole('merchant')
  }

  const hasInternalPrivileges = () => {
    return isAdmin() || isDeveloper()
  }

  const profile = () => {
    return state.user.profile.attributes
  }

  const name = () => {
    return state.user && state.user.name()
  }

  const fullName = () => {
    return state.user.fullName()
  }

  const initial = () => {
    return state.user.initial()
  }

  const avatarUrl = () => {
    return state.user.avatarUrl()
  }

  const avatarIndex = () => {
    return state.avatarIndex
  }

  const currentLat = () => {
    const coords = state.position.coords || {}
    return coords.latitude // type double
  }

  const currentLong = () => {
    const coords = state.position.coords || {}
    return coords.longitude // type double
  }

  const loadAvatarIndex = async () => {
    const avatarIndex = await anearApi.getGuestAvatarIndex()
    gset({ avatarIndex })
    return avatarIndex
  }

  const mobileSafari = () => {
    const ua = window.navigator.userAgent
    const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream
    const webkit = !!ua.match(/WebKit/i)

    return iOS && webkit && !ua.match(/CriOS/i)
  }

  const canAccessCamera = () => {
    return navigator.mediaDevices && navigator.mediaDevices.getUserMedia
  }

  return {
    anearUser,
    avatarIndex,
    loadAvatarIndex,
    avatarUrl,
    authMeta,
    authTokenPayload,
    acctType,
    canAccessCamera,
    clearAuthToken,
    clearUser,
    clearUserAndToken,
    dismissGuestReestablish,
    currentLat,
    currentLong,
    fullName,
    hasAuthToken,
    hasInternalPrivileges,
    hasRegisteredToken,
    hasRole,
    initial,
    isAdmin,
    isAuthed,
    isDeveloper,
    isMerchant,
    isGuestAuthed,
    isRegisteredAuthed,
    loadUser,
    mobileSafari,
    name,
    profile,
    roles,
    setPosition,
    setUser,
    state,
    guestReestablishMeta,
  }
}

const UserAuthContainer = createContainer(useUserAuth)
export default UserAuthContainer
